import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';

export interface SelectOption<T extends string | number = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  options?: SelectOption[];
  placeholder?: string;
  fieldClassName?: string;
  children?: React.ReactNode;
}

const SELECT_BASE =
  'w-full h-10 pl-3 pr-9 rounded-[10px] text-[14px] appearance-none bg-white border border-[rgba(15,23,21,.12)] text-[var(--rt-ink-900)] focus:outline-none focus:border-[var(--rt-brand-500)] focus:ring-2 focus:ring-[var(--rt-brand-500)]/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed dark:bg-transparent dark:border-white/10 dark:text-white dark:focus:border-[var(--rt-brand-400)]';

export const SelectField = React.forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      label,
      hint,
      error,
      options,
      placeholder,
      required,
      className,
      fieldClassName,
      id,
      children,
      ...rest
    },
    ref,
  ) => {
    const autoId = React.useId();
    const fieldId = id ?? autoId;
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={fieldId}
            className="text-[12px] font-medium text-[var(--rt-ink-700)] dark:text-zinc-300"
          >
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={fieldId}
            required={required}
            aria-invalid={error ? 'true' : undefined}
            {...rest}
            className={cn(
              SELECT_BASE,
              error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
              fieldClassName,
            )}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options?.map((o) => (
              <option key={String(o.value)} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
            {children}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--rt-ink-400)] pointer-events-none"
            aria-hidden="true"
          />
        </div>
        {error ? (
          <span className="text-[11.5px] text-red-600 dark:text-red-400">
            {error}
          </span>
        ) : hint ? (
          <span className="text-[11.5px] text-[var(--rt-ink-500)] dark:text-zinc-400">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);
SelectField.displayName = 'SelectField';

export default SelectField;
